<template>
  <Steps
    ref="formWizard"
    :steps="formSteps"
    :is-submitting="postNewCampaign.isLoading.value"
    @before-move="validateStep"
    @on-complete="submitNewCampaign('view')"
  >
    <template #step-1>
      <FormGroup>
        <RadioBoxed
          v-model="newCampaign.importType"
          :options="[
            { value: 'manual', label: 'Manual' },
            { value: 'vrmSearch', label: 'VRM search' }
          ]"
          :has-error="v$.importType.$error"
        />
        <InputError :has-error="v$.importType.$error">{{ v$.importType.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        :label="$t('ui.common.type')"
        :is-required="v$.campaignType.required"
      >
        <RadioTabbed
          v-model="newCampaign.campaignType"
          :options="[
            { label: 'Standard', value: 'standard' },
            { label: 'Marketing', value: 'marketing' }
          ]"
          :selected-option="newCampaign.campaignType"
          :class="{ 'has-error': v$.campaignType.$error }"
        />
        <InputError :has-error="v$.campaignType.$error">{{ v$.campaignType.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        :label="$t('ui.common.name')"
        :is-required="v$.name.required"
      >
        <Input
          v-model="newCampaign.name"
          :has-error="v$.name.$error"
        />
        <InputError :has-error="v$.name.$error">{{ v$.name.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        :label="$t('ui.entities.dealership', 2)"
        :is-required="v$.dealerships.required"
      >
        <Select
          v-model="newCampaign.dealerships"
          :options="dealershipsList"
          :chips="false"
          mode="multiple"
          handle-search
          searchable
          value-key="value"
          label-key="label"
          class="w-full"
          :has-error="v$.dealerships.$error"
        >
          <template #option="{ option }">
            <div class="flex items-center">
              <img
                :src="option.brandLogo"
                class="mr-3 w-6"
              />
              {{ option.label }}
            </div>
          </template>
        </Select>
        <InputError :has-error="v$.dealerships.$error">{{ v$.dealerships.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <div class="grid grid-cols-2 gap-4">
        <FormGroup
          :label="$t('ui.entities.financeAgreement.startDate')"
          :is-required="v$.startDate.required"
        >
          <DatePicker
            v-model="newCampaign.startDate"
            mode="single"
            display-as="input"
            display-format="full"
            :has-error="v$.startDate.$error"
          />
          <InputError :has-error="v$.startDate.$error">{{ v$.startDate.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          :label="$t('ui.entities.financeAgreement.endDate')"
          :is-required="v$.endDate.required"
        >
          <DatePicker
            v-model="newCampaign.endDate"
            mode="single"
            display-as="input"
            display-format="full"
            :has-error="v$.endDate.$error"
          />
          <InputError :has-error="v$.endDate.$error">{{ v$.endDate.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
    </template>

    <template #step-2>
      <div
        v-if="newCampaign.importType == 'manual'"
        class="grid grid-cols-2 gap-x-6"
      >
        <FormGroup
          label="Months Had"
          :is-required="v$.filterMonthsHadMin.required && v$.filterMonthsHadMax.required"
        >
          <div class="flex gap-2 items-center w-44">
            <div>
              <Input
                v-model="newCampaign.filterMonthsHadMin"
                placeholder="From"
                :has-error="v$.filterMonthsHadMin.$error"
              />
              <InputError :has-error="v$.filterMonthsHadMin.$error">
                {{ v$.filterMonthsHadMin.$errors[0]?.$message }}
              </InputError>
            </div>
            <Icon type="arrow-right" />
            <div>
              <Input
                v-model="newCampaign.filterMonthsHadMax"
                placeholder="To"
                :has-error="v$.filterMonthsHadMax.$error"
              />
              <InputError :has-error="v$.filterMonthsHadMax.$error">
                {{ v$.filterMonthsHadMax.$errors[0]?.$message }}
              </InputError>
            </div>
          </div>
        </FormGroup>
        <FormGroup
          label="Months Remaining"
          :is-required="v$.filterMonthsRemainingMin.required && v$.filterMonthsRemainingMax.required"
        >
          <div class="flex gap-2 items-center w-44">
            <div>
              <Input
                v-model="newCampaign.filterMonthsRemainingMin"
                placeholder="From"
                :has-error="v$.filterMonthsRemainingMin.$error"
              />
              <InputError :has-error="v$.filterMonthsRemainingMin.$error">
                {{ v$.filterMonthsRemainingMin.$errors[0]?.$message }}
              </InputError>
            </div>
            <Icon type="arrow-right" />
            <div>
              <Input
                v-model="newCampaign.filterMonthsRemainingMax"
                placeholder="To"
                :has-error="v$.filterMonthsRemainingMax.$error"
              />
              <InputError :has-error="v$.filterMonthsRemainingMax.$error">
                {{ v$.filterMonthsRemainingMax.$errors[0]?.$message }}
              </InputError>
            </div>
          </div>
        </FormGroup>

        <FormGroup
          label="OTRP"
          :is-required="v$.filterOTRPMin.required && v$.filterOTRPMin.required"
        >
          <div class="flex gap-2 items-center w-44">
            <div>
              <Input
                v-model="newCampaign.filterOTRPMin"
                placeholder="From"
                :has-error="v$.filterOTRPMin.$error"
              />
              <InputError :has-error="v$.filterOTRPMin.$error">{{ v$.filterOTRPMin.$errors[0]?.$message }}</InputError>
            </div>
            <Icon type="arrow-right" />
            <div>
              <Input
                v-model="newCampaign.filterOTRPMax"
                placeholder="To"
                :has-error="v$.filterOTRPMax.$error"
              />
              <InputError :has-error="v$.filterOTRPMax.$error">{{ v$.filterOTRPMax.$errors[0]?.$message }}</InputError>
            </div>
          </div>
        </FormGroup>

        <FormGroup
          :label="$t('ui.entities.vehicle.newUsed')"
          :is-required="v$.filterNewUsed.required"
        >
          <RadioTabbed
            v-model="newCampaign.filterNewUsed"
            :options="[
              { label: 'New', value: 'new' },
              { label: 'Used', value: 'used' },
              { label: 'Both', value: 'both' }
            ]"
            :selected-option="newCampaign.filterNewUsed"
            :class="{ 'has-error': v$.filterNewUsed.$error }"
          />
          <InputError :has-error="v$.filterNewUsed.$error">{{ v$.filterNewUsed.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <!-- Fuel Type filter commented out as it is unreliable -->
        <!-- <FormGroup
          label="Fuel Type"
          :is-required="v$.filterFuelType.required && v$.filterFuelType.required"
        >
          <div>
            <Select
              v-model="newCampaign.filterFuelType"
              :options="[
                { label: 'Petrol', value: 'petrol' },
                { label: 'Diesel', value: 'diesel' },
                { label: 'HEV', value: 'hev' },
                { label: 'PHEV', value: 'phev' },
                { label: 'Electric', value: 'electric' }
              ]"
              mode="multiple"
              chips
              value-key="value"
              label-key="label"
              :max-range="authStore.roles.includes(2) && authStore.roles.length == 1 ? 30 : null"
              class="w-full"
              :class="{ 'has-error': v$.filterFuelType.$error }"
            ></Select>
            <InputError :has-error="v$.filterFuelType.$error">{{ v$.filterFuelType.$errors[0]?.$message }}</InputError>
          </div>
        </FormGroup> -->

        <FormGroup
          :label="$t('ui.entities.financeAgreement.financeProduct')"
          :is-required="v$.filterFinanceProducts.required"
        >
          <Select
            v-model="newCampaign.filterFinanceProducts"
            :options="['PCP', 'HP', 'CH', 'BCH', 'PCH']"
            chips
            mode="multiple"
            class="w-full"
            :has-error="v$.filterFinanceProducts.$error"
          ></Select>

          <InputError :has-error="v$.filterFinanceProducts.$error">
            {{ v$.filterFinanceProducts.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          label="Status"
          :is-required="v$.filterStatus.required"
        >
          <Select
            v-model="newCampaign.filterStatus"
            :options="agreementStatusOptions"
            mode="multiple"
            searchable
            chips
            value-key="key"
            label-key="key"
            :max-range="authStore.roles.includes(2) && authStore.roles.length == 1 ? 30 : null"
            class="w-full"
            :class="{ 'has-error': v$.filterStatus.$error }"
          ></Select>
          <InputError :has-error="v$.filterStatus.$error">{{ v$.filterStatus.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Contact methods"
          :is-required="v$.availableContactMethods.required || v$.unavailableContactMethods.required"
          class="col-span-2"
        >
          <div
            class="flex items-center gap-2"
            :class="!contactMethodsIsChecked ? 'text-quaternary' : ''"
          >
            <Checkbox
              v-model="contactMethodsIsChecked"
              @change="toggleContactMethodsFilter()"
            />
            <div>Customers that</div>
            <Select
              v-model="contactMethodsFilter.term"
              :options="['Have', 'Haven\'t']"
              placeholder="..."
              class="max-w-32"
              :readonly="!contactMethodsIsChecked"
              :has-error="vContactMethods$.term.$error"
            />
            <div>got</div>
            <Select
              v-model="contactMethodsFilter.value"
              :options="[
                { value: 'email', label: 'Email' },
                { value: 'sms', label: 'SMS' },
                { value: 'phone', label: 'Phone' }
              ]"
              mode="multiple"
              placeholder="..."
              class="max-w-[160px]"
              :readonly="!contactMethodsIsChecked"
              :has-error="vContactMethods$.value.$error"
            />
          </div>
        </FormGroup>
      </div>
      <!-- <div v-else-if="newCampaign.importType == 'upload'">
        <FormGroup :label="$t('ui.entities.import.importSource')" :is-required="v$.importSource.required">
          <Select v-model="newCampaign.importSource" :options="[{ value: 'k2k', label: 'K2K' }]" readonly />
          <InputError :has-error="v$.importSource.$error">{{ v$.importSource.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <div class="col-span-2">
          <FileUpload
            :api-endpoint="fileUploadEndpoint"
            :max-file-size="2000000"
            :accepted-file-types="['text/csv']"
            button-position="full-width"
            @uploaded="newCampaign.isFileUploadValid = true"
          />
          <InputError :has-error="v$.isFileUploadValid.$error">{{ v$.isFileUploadValid.$errors[0]?.$message }}</InputError>
        </div>
      </div> -->
      <div v-else-if="newCampaign.importType == 'vrmSearch'">
        <FormGroup
          label="VRMs"
          :is-required="v$.filterVRMString.required"
          :info="VRMInfoText"
        >
          <TextArea
            v-model="newCampaign.filterVRMString"
            rows="2"
            :has-error="v$.filterVRMString.$error"
          />
          <InputError :has-error="v$.filterVRMString.$error">{{ v$.filterVRMString.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
    </template>

    <template #step-3>
      <div class="flex flex-col gap-2 mb-6">
        <div class="text-2xl">{{ newCampaign.name }}</div>
        <div class="flex items-center gap-2">
          <span>{{ dayjs(newCampaign.startDate).format('Do MMM YYYY') }}</span>
          <Icon
            type="arrow-right"
            class="text-sm text-quaternary"
          />
          <span>{{ dayjs(newCampaign.endDate).format('Do MMM YYYY') }}</span>
        </div>
        <div class="flex gap-2 flex-wrap">
          <div
            v-for="(dealership, index) in newCampaign.dealerships"
            :key="index"
          >
            <Chip size="sm">
              <img
                :src="dealershipsList.find(d => d.value == dealership).brandLogo"
                width="20"
                class="mr-2"
              />
              {{ dealershipsList.find(d => d.value == dealership).label }}
            </Chip>
          </div>
        </div>
      </div>
      <ListGroup
        v-if="newCampaign.importType == 'manual'"
        class="mb-4"
      >
        <ListGroupItem class="flex justify-between">
          <div>Months Had</div>
          <div v-if="newCampaign.filterMonthsHadMin && newCampaign.filterMonthsHadMax">
            {{ newCampaign.filterMonthsHadMin }} - {{ newCampaign.filterMonthsHadMax }} months
          </div>
          <div v-else-if="newCampaign.filterMonthsHadMin">{{ newCampaign.filterMonthsHadMin }} months or more</div>
          <div v-else-if="newCampaign.filterMonthsHadMax">{{ newCampaign.filterMonthsHadMax }} months or less</div>
          <div v-else>Any</div>
        </ListGroupItem>
        <ListGroupItem class="flex justify-between">
          <div>Months Remaining</div>
          <div v-if="newCampaign.filterMonthsRemainingMin && newCampaign.filterMonthsRemainingMax">
            {{ newCampaign.filterMonthsRemainingMin }} - {{ newCampaign.filterMonthsRemainingMax }} months
          </div>
          <div v-else-if="newCampaign.filterMonthsRemainingMin">
            {{ newCampaign.filterMonthsRemainingMin }} months or more
          </div>
          <div v-else-if="newCampaign.filterMonthsRemainingMax">
            {{ newCampaign.filterMonthsRemainingMax }} months or less
          </div>
          <div v-else>Any</div>
        </ListGroupItem>
        <ListGroupItem class="flex justify-between">
          <div>{{ $t('ui.entities.vehicle.newUsed') }}</div>
          <div :class="{ capitalize: !newCampaign.filterNewUsed == 'both' }">
            {{ newCampaign.filterNewUsed == 'both' ? 'New & Used' : newCampaign.filterNewUsed }}
          </div>
        </ListGroupItem>
        <ListGroupItem class="flex justify-between">
          <div>{{ $t('ui.entities.financeAgreement.financeProduct') }}</div>
          <div
            v-if="newCampaign.filterFinanceProducts"
            class="flex gap-2"
          >
            <div
              v-for="(type, index) in newCampaign.filterFinanceProducts"
              :key="index"
            >
              {{ type }}
            </div>
          </div>
          <div v-else>Any</div>
        </ListGroupItem>
      </ListGroup>
      <ListGroup
        v-else-if="newCampaign.importType == 'vrmSearch'"
        class="mb-4"
      >
        <ListGroupItem class="flex justify-between">
          <div>VRMs searched</div>
          <div>{{ vrmSearchList.length }}</div>
        </ListGroupItem>
      </ListGroup>
      <Alert
        message="The number of customers added to the campaign might differ from the total number of VRMs, if customers are no longer active."
        :show-close-button="false"
        :show-dismiss-button="false"
      />

      <div
        v-if="newCampaign.importType != 'vrmSearch'"
        class="flex flex-col gap-2 items-end"
      >
        <div class="text-quaternary">Customers Matched</div>
        <Icon
          v-if="filterResultsApiRequest.isLoading.value"
          type="spinner-third"
          class="btn-icon fa-spin"
          fa-style="far"
        />
        <div
          v-else
          class="text-2xl font-medium -mt-2"
        >
          {{ matchedCustomersCount || 0 }}
        </div>
      </div>
    </template>
    <template #completeAction>
      <ButtonGroup>
        <Button
          label="Save & View"
          :is-loading="postNewCampaign.isLoading.value"
          @click="submitNewCampaign('view')"
        />
        <Dropdown position="bottom-end">
          <template #triggerContent>
            <Button icon="chevron-down" />
          </template>
          <DropdownItem
            class="bg-brand"
            @click="submitNewCampaign('addNew')"
          >
            Save & Add New
          </DropdownItem>
        </Dropdown>
      </ButtonGroup>
    </template>
  </Steps>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { alertToast } from '@/utilities/notification'

import Steps from '@/components/steps/Steps.vue'

import Icon from '@/components/icon/Icon.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import Chip from '@/components/chip/Chip.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import InputError from '@/components/forms/InputError.vue'
import Select from '@/components/forms/Select.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Button from '@/components/button/Button.vue'
import TextArea from '@/components/forms/TextArea.vue'
import RadioBoxed from '@/components/forms/RadioBoxed.vue'
import ButtonGroup from '@/components/button/ButtonGroup.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import Checkbox from '@/components/forms/Checkbox.vue'

import useVuelidate from '@vuelidate/core'
import { required, requiredIf, numeric } from '@vuelidate/validators'

import useApiRequest from '@/composables/useApiRequest'
import { useAuthStore } from '@/stores/AuthStore'
import { getDictionaryOptions } from '@/dictionary'
import Alert from '@/components/alert/Alert.vue'

const postNewCampaign = useApiRequest()
const filterResultsApiRequest = useApiRequest()
const authStore = useAuthStore()
const formWizard = ref(null) // Reference to the FormWizard component

const dealershipsApiRequest = useApiRequest()
const dealershipsList = ref(null)
const dealershipsAllowed = authStore.accessAllowed.dealerships.map(dealership => dealership.id)

const matchedCustomersCount = ref(null)

const emit = defineEmits(['form-updated', 'is-submitting', 'completed'])

const VRMInfoText =
  'VRMs can be separated by a comma, space or new line. Please ensure there are no gaps within each VRM.'

// const fileUploadEndpoint = '/example'

const newCampaignConfig = {
  importType: 'manual',
  campaignType: 'standard',
  name: null,
  dealerships: [],
  startDate: '',
  endDate: '',
  filterMonthsHadMin: null,
  filterMonthsHadMax: null,
  filterMonthsRemainingMin: null,
  filterMonthsRemainingMax: null,
  filterNewUsed: 'both',
  filterFinanceProducts: [],
  filterOTRPMin: null,
  filterOTRPMax: null,
  filterVRMString: null,
  filterVRM: null,
  // filterFuelType: [], // Commented out as it is unreliable
  filterStatus: [],
  isFileUploadValid: false,
  importSource: 'k2k',
  availableContactMethods: [],
  unavailableContactMethods: []
}

const newCampaign = ref({ ...newCampaignConfig })

const agreementStatusOptions = getDictionaryOptions('agreementStatus')

const { t } = useI18n()

const formSteps = computed(() => [
  {
    label: t('ui.forms.basicDetails'),
    title: t('ui.forms.basicDetails'),
    icon: null
  },
  {
    label: t('ui.forms.importCustomers'),
    title: t('ui.forms.importCustomers'),
    icon: null
  },
  {
    label: t('ui.forms.summary'),
    title: t('ui.forms.summary'),
    icon: null
  }
])

const inputRules = {
  formStep1: {
    campaignType: { required },
    importType: { required },
    name: { required },
    dealerships: { required },
    startDate: { required },
    endDate: { required }
  },
  formStep2: {
    manualRules: {
      filterMonthsHadMin: { numeric },
      filterMonthsHadMax: { numeric },
      filterMonthsRemainingMin: { numeric },
      filterMonthsRemainingMax: { numeric },
      filterNewUsed: { required },
      filterFinanceProducts: { required },
      filterOTRPMin: { numeric },
      filterOTRPMax: { numeric },
      // filterFuelType: {}, // Commented out as it is unreliable
      filterStatus: {},
      availableContactMethods: {},
      unavailableContactMethods: {}
    },
    uploadRules: {
      isFileUploadValid: {
        required
      },
      importSource: {
        required
      }
    },
    vrmSearchRules: {
      filterVRMString: { required }
    }
  }
}

const vrmSearchList = computed(() => {
  return newCampaign.value.filterVRMString.split(/[\s,]+/).filter(Boolean) || null
})

const contactMethodsFilter = ref({
  term: null,
  value: null
})
const contactMethodsIsChecked = ref(false)

const contactMethodsRules = {
  term: {
    required: requiredIf(function () {
      return contactMethodsIsChecked.value
    })
  },
  value: {
    required: requiredIf(function () {
      return contactMethodsIsChecked.value
    })
  }
}

function toggleContactMethodsFilter() {
  if (!contactMethodsIsChecked.value) {
    contactMethodsFilter.value.term = null
    contactMethodsFilter.value.value = []
  }
}

const stepValidationRules = computed(() => {
  if (formWizard.value?.currentStep) {
    // console.log(`formStep${formWizard.value?.currentStep}`)
    if (formWizard.value?.currentStep == 1) {
      if (newCampaign.value.importType == 'manual') {
        return inputRules[`formStep${formWizard.value?.currentStep + 1}`].manualRules
      }
      // if (newCampaign.value.importType == 'upload') {
      //   return inputRules[`formStep${formWizard.value?.currentStep + 1}`].uploadRules
      // }
      if (newCampaign.value.importType == 'vrmSearch') {
        return inputRules[`formStep${formWizard.value?.currentStep + 1}`].vrmSearchRules
      }
    }
    return inputRules[`formStep${formWizard.value?.currentStep + 1}`]
  }
  return inputRules.formStep1
})

const v$ = useVuelidate(stepValidationRules, newCampaign)
const vContactMethods$ = useVuelidate(contactMethodsRules, contactMethodsFilter)

async function validateStep() {
  if (formWizard.value.currentStep == 1) {
    const isStepCorrect = await v$.value.$validate()
    const isContactMethodsCorrect = await vContactMethods$.value.$validate()
    if (isStepCorrect && isContactMethodsCorrect) {
      if (newCampaign.value.importType != 'vrmSearch') {
        fetchMatchedCustomers()
      }
      formWizard.value.move(1) // Move to the next step
    }
  } else {
    const isStepCorrect = await v$.value.$validate()
    if (isStepCorrect) {
      formWizard.value.move(1) // Move to the next step
    }
  }
}

function fetchMatchedCustomers() {
  const dataToPost = ref(null)

  if (newCampaign.value.importType == 'manual') {
    dataToPost.value = {
      name: newCampaign.value.name,
      type: newCampaign.value.campaignType,
      dealerships: newCampaign.value.dealerships,
      startDate: newCampaign.value.startDate,
      endDate: newCampaign.value.endDate,
      filterNewUsed: newCampaign.value.filterNewUsed,
      filterFinanceProducts: newCampaign.value.filterFinanceProducts.length
        ? newCampaign.value.filterFinanceProducts
        : null,
      filterMonthsHadMin: newCampaign.value.filterMonthsHadMin || null,
      filterMonthsHadMax: newCampaign.value.filterMonthsHadMax || null,
      filterMonthsRemainingMin: newCampaign.value.filterMonthsRemainingMin || null,
      filterMonthsRemainingMax: newCampaign.value.filterMonthsRemainingMax || null,
      filterOTRPMin: newCampaign.value.filterOTRPMin || null,
      filterOTRPMax: newCampaign.value.filterOTRPMax || null,
      filterStatus: newCampaign.value.filterStatus.length ? newCampaign.value.filterStatus : null,
      // filterFuelType: newCampaign.value.filterFuelType.length ? newCampaign.value.filterFuelType : null, // Commented out as it is unreliable
      availableContactMethods: contactMethodsFilter.value.term == 'Have' ? contactMethodsFilter.value.value : null,
      unavailableContactMethods: contactMethodsFilter.value.term == "Haven't" ? contactMethodsFilter.value.value : null
    }
  }

  // if (newCampaign.value.importType == 'upload') {
  //   dataToPost.value = {}
  // }

  try {
    filterResultsApiRequest
      .send({
        method: 'POST',
        endpoint: '/v1/campaigns/new-matched-customers',
        data: JSON.stringify(dataToPost.value)
      })
      .then(response => {
        if (response.success) {
          matchedCustomersCount.value = response.data.metadata.pagination.total
        } else {
          alertToast('Error', 'Failed to fetch matched customers', 'error')
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
}

try {
  dealershipsApiRequest
    .send({ method: 'GET', endpoint: '/v1/dealerships?ids=' + dealershipsAllowed })
    .then(response => {
      if (response.success) {
        dealershipsList.value = response.data.map(dealership => {
          return {
            value: dealership.id,
            label: dealership.displayName,
            brandLogo: dealership.branding?.logoUrl
          }
        })
      } else {
        alertToast('Failed to fetch dealerships', response.message, 'error')
      }
    })
} catch (err) {
  alertToast('Failed to fetch dealerships', err.message, 'error')
}

function submitNewCampaign(action) {
  emit('is-submitting', true)
  if (newCampaign.value.importType == 'manual' && matchedCustomersCount.value == 0) {
    alertToast('No customers matched', null, 'info')
    return
  }

  if (matchedCustomersCount.value > 800) {
    alertToast('Too many customers', 'Keep campaigns to under 800 customers', 'warning')
    return
  }

  emit('is-submitting', true)
  const dataToPost = ref(null)

  if (newCampaign.value.importType == 'manual') {
    dataToPost.value = {
      name: newCampaign.value.name,
      type: newCampaign.value.campaignType,
      dealerships: newCampaign.value.dealerships,
      startDate: newCampaign.value.startDate,
      endDate: newCampaign.value.endDate,
      filterNewUsed: newCampaign.value.filterNewUsed,
      filterFinanceProducts: newCampaign.value.filterFinanceProducts.length
        ? newCampaign.value.filterFinanceProducts
        : null,
      filterMonthsHadMin: newCampaign.value.filterMonthsHadMin || null,
      filterMonthsHadMax: newCampaign.value.filterMonthsHadMax || null,
      filterMonthsRemainingMin: newCampaign.value.filterMonthsRemainingMin || null,
      filterMonthsRemainingMax: newCampaign.value.filterMonthsRemainingMax || null,
      filterOTRPMin: newCampaign.value.filterOTRPMin || null,
      filterOTRPMax: newCampaign.value.filterOTRPMax || null,
      filterStatus: newCampaign.value.filterStatus.length ? newCampaign.value.filterStatus : null,
      // filterFuelType: newCampaign.value.filterFuelType.length ? newCampaign.value.filterFuelType : null, // Commented out as it is unreliable
      availableContactMethods: contactMethodsFilter.value.term == 'Have' ? contactMethodsFilter.value.value : null,
      unavailableContactMethods: contactMethodsFilter.value.term == "Haven't" ? contactMethodsFilter.value.value : null
    }
  }
  if (newCampaign.value.importType == 'vrmSearch') {
    dataToPost.value = {
      name: newCampaign.value.name,
      type: newCampaign.value.campaignType,
      dealerships: newCampaign.value.dealerships,
      startDate: newCampaign.value.startDate,
      endDate: newCampaign.value.endDate,
      filterVRM: vrmSearchList.value
    }
  }

  try {
    postNewCampaign
      .send({ method: 'POST', endpoint: '/v1/campaigns', data: JSON.stringify(dataToPost.value) })
      .then(response => {
        emit('is-submitting', false)
        if (response.success) {
          if (action == 'addNew') {
            emit('completed', { action: 'addNew', id: null })
          } else {
            emit('completed', { action: 'view', id: response.data.campaign_id })
          }
          alertToast('Campaign Added', 'The campaign was successfully created', 'success')
        } else {
          alertToast('Failed to add new campaign', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
}

watch(
  () => newCampaign.value,
  () => {
    emit('form-updated')
  },
  { deep: true }
)
</script>

<style></style>
